import { CartAddEvent, ThemeEvents } from '@theme/events';
import {
  applyAttachBoost,
  composeSlots,
  computeDominantGender,
  decisionTree,
  filterCandidatesWithFallback,
  formatProductTitle,
  normalizeCategory,
  normalizeGender,
  renderPriceHTML,
  getBucket,
  selectPriorityRec,
} from '@theme/cart-upsell-engine';
import {
  fetchAttachScores,
  fetchCartProductMetafields,
  fetchProductVariants,
  hydratePool,
  hydrateWearWithProducts,
  logAdd,
  logClick,
  logImpression,
} from '@theme/cart-upsell-api';

/** @typedef {import('./cart-upsell-engine').UpsellCandidate} UpsellCandidate */

/** @type {CartUpsellComponent | null} */
let activeUpsell = null;
let refreshToken = 0;
let poolsPrefetchStarted = false;

class CartUpsellComponent extends HTMLElement {
  #abortController = new AbortController();
  #layoutTimer = null;
  #lastImpressionKey = '';
  #lastRecs = [];
  #lastCartKey = '';
  #desktopMedia = window.matchMedia('(min-width: 750px)');
  #resizeObserver = null;

  connectedCallback() {
    if (!window.afendsUpsell) return;

    activeUpsell = this;
    this.#lastImpressionKey = sessionStorage.getItem('afends_last_upsell_impression_key') || '';
    this.#restoreCachedRecs();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('cart:drawer-open', this.#handleDrawerOpen, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('resize', this.#scheduleLayout, { signal });

    this.#initScroller(signal);
    prefetchUpsellPools();
    scheduleGlobalRefresh(0);
  }

  disconnectedCallback() {
    if (activeUpsell === this) activeUpsell = null;
    this.#abortController.abort();
    if (this.#layoutTimer) clearTimeout(this.#layoutTimer);
    this.#resizeObserver?.disconnect();
  }

  /**
   * Apply recommendations onto the live upsell instance (survives cart morph).
   * @param {UpsellCandidate[]} recs
   * @param {string} cartKey
   * @param {Record<string, unknown> | null} [meta]
   */
  applyRecs(recs, cartKey, meta = null) {
    const merged = recs.map((rec) => {
      if (rec.all_variants?.length) return rec;
      const existing = this.#lastRecs.find(
        (prior) => prior.product_id === rec.product_id || prior.variant_id === rec.variant_id
      );
      if (existing?.all_variants?.length) {
        return { ...rec, all_variants: existing.all_variants };
      }
      return rec;
    });

    const impressionKey = merged.map((rec) => rec.variant_id).join(',');
    const shouldLogImpression =
      Boolean(meta) && Boolean(impressionKey) && impressionKey !== this.#lastImpressionKey;

    if (shouldLogImpression) {
      this.#lastImpressionKey = impressionKey;
      try {
        sessionStorage.setItem('afends_last_upsell_impression_key', impressionKey);
      } catch {
        /* private mode / quota */
      }
    }

    this.#persistCachedRecs(merged, cartKey);
    this.#renderCarousel(merged, { skipCache: true });

    // Fire after slots are composed and painted so analytics match what the shopper saw.
    if (shouldLogImpression && meta) logImpression(meta);
  }

  #restoreCachedRecs() {
    try {
      const cached = sessionStorage.getItem('afends_upsell_last_recs');
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed?.recs) || !parsed.cartKey) return;
      this.#lastRecs = parsed.recs;
      this.#lastCartKey = parsed.cartKey;
      if (this.#lastRecs.length) {
        this.#renderCarousel(this.#lastRecs, { skipCache: true });
      }
    } catch {
      /* ignore corrupt cache */
    }
  }

  #persistCachedRecs(recs, cartKey) {
    this.#lastRecs = recs;
    this.#lastCartKey = cartKey;
    try {
      sessionStorage.setItem(
        'afends_upsell_last_recs',
        JSON.stringify({ cartKey, recs })
      );
    } catch {
      /* quota / private mode */
    }
  }

  #handleDrawerOpen = () => {
    if (this.#lastRecs.length) {
      this.#renderCarousel(this.#lastRecs, { skipCache: true });
    }
    scheduleGlobalRefresh(0);
  };

  #handleCartAdd = () => {
    scheduleGlobalRefresh(0);
  };

  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    scheduleGlobalRefresh(event.detail?.data?.source === 'cart-upsell' ? 0 : 0);
  };

  /**
   * @param {UpsellCandidate[]} recs
   * @param {{ skipCache?: boolean }} [options]
   */
  #renderCarousel(recs, options = {}) {
    const track = this.querySelector('[data-cart-upsell-track]');
    if (!(track instanceof HTMLElement)) return;

    if (!recs.length) {
      this.hidden = true;
      track.innerHTML = '';
      return;
    }

    track.innerHTML = recs
      .map((rec, index) => this.#renderCard(rec, index + 1, index < 3 ? 'eager' : 'lazy'))
      .join('');

    if (!options.skipCache) {
      this.#lastRecs = recs;
    }

    this.hidden = false;
    this.#scheduleLayout();
  }

  #scheduleLayout = () => {
    clearTimeout(this.#layoutTimer);
    this.#layoutTimer = window.setTimeout(() => {
      requestAnimationFrame(() => {
        this.#updateSlideWidth();
        this.#updateArrows();
        requestAnimationFrame(() => {
          this.#updateSlideWidth();
          this.#updateArrows();
        });
      });
    }, 0);
  };

  #updateSlideWidth = () => {
    const scroller = this.querySelector('[data-cart-upsell-scroller]');
    if (!(scroller instanceof HTMLElement) || scroller.clientWidth <= 0) return;

    const slideWidth = Math.round(scroller.clientWidth * 0.88);
    this.style.setProperty('--cart-upsell-slide-width', `${slideWidth}px`);
  };

  /**
   * @param {AbortSignal} signal
   */
  #initScroller(signal) {
    const wrapper = this.querySelector('.cart-upsell__scroller-wrap');
    const scroller = wrapper?.querySelector('[data-cart-upsell-scroller]');
    if (!(wrapper instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    const leftArrow = wrapper.querySelector('[data-scroll-left]');
    const rightArrow = wrapper.querySelector('[data-scroll-right]');

    leftArrow?.addEventListener('click', () => this.#scrollToAlignedSlide(-1), { signal });
    rightArrow?.addEventListener('click', () => this.#scrollToAlignedSlide(1), { signal });
    scroller.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal });
    this.#desktopMedia.addEventListener('change', () => this.#scheduleLayout(), { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleLayout());
    this.#resizeObserver.observe(scroller);
  }

  /**
   * Scroll so the next/previous slide is left-aligned in the scroller.
   * @param {-1 | 1} direction
   */
  #scrollToAlignedSlide(direction) {
    const scroller = this.querySelector('[data-cart-upsell-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const slides = [...scroller.querySelectorAll('.cart-upsell__slide')].filter(
      (slide) => slide instanceof HTMLElement
    );
    if (!slides.length) return;

    const scrollLeft = scroller.scrollLeft;
    const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const tolerance = 2;

    /** @type {HTMLElement | null} */
    let target = null;

    if (direction > 0) {
      target =
        slides.find((slide) => slide.offsetLeft > scrollLeft + tolerance) ||
        slides[slides.length - 1];
    } else {
      for (let index = slides.length - 1; index >= 0; index -= 1) {
        const slide = slides[index];
        if (slide.offsetLeft < scrollLeft - tolerance) {
          target = slide;
          break;
        }
      }
      target = target || slides[0];
    }

    const nextLeft = Math.min(Math.max(target.offsetLeft, 0), maxLeft);
    scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }

  #updateArrows = () => {
    const wrapper = this.querySelector('.cart-upsell__scroller-wrap');
    const scroller = wrapper?.querySelector('[data-cart-upsell-scroller]');
    const leftArrow = wrapper?.querySelector('[data-scroll-left]');
    const rightArrow = wrapper?.querySelector('[data-scroll-right]');

    if (!(scroller instanceof HTMLElement)) return;
    if (!(leftArrow instanceof HTMLElement) || !(rightArrow instanceof HTMLElement)) return;

    const maxLeft = scroller.scrollWidth - scroller.clientWidth;
    const canScroll = maxLeft > 2;
    leftArrow.classList.toggle('is-hidden', !canScroll || scroller.scrollLeft <= 2);
    rightArrow.classList.toggle('is-hidden', !canScroll || scroller.scrollLeft >= maxLeft - 2);
  };

  /**
   * @param {UpsellCandidate} rec
   * @param {number} position
   * @param {'eager' | 'lazy'} loading
   */
  #renderCard(rec, position, loading) {
    const { primary, secondary, colour } = formatProductTitle(rec.product_title);
    const variants = sortUpsellVariants(rec.all_variants || []);
    const variantsPending = variants.length === 0;
    const isSingleVariant = variants.length <= 1;
    const numericVariantId = rec.variant_id.replace('gid://shopify/ProductVariant/', '');
    const selectedVariant = variants.find((variant) => variant.variant_id === rec.variant_id) || variants[0];
    const available = variantsPending
      ? rec.available !== false
      : isSingleVariant
        ? Boolean(selectedVariant?.available)
        : variants.some((variant) => variant.available);
    const addDisabled = !available;
    const price = selectedVariant?.price ?? rec.price;
    const compareAtPrice = selectedVariant?.compare_at_price ?? rec.compare_at_price;

    const source = rec.source || '';
    const variantButtons = !isSingleVariant
      ? `<div class="cart-upsell__variants" data-cart-upsell-variants hidden>
           ${variants
             .map((variant) => {
               const numericId = variant.variant_id.replace('gid://shopify/ProductVariant/', '');
               const label = variant.size || variant.title;
               return `<button
                 type="button"
                 class="cart-upsell__variant-button"
                 data-variant-id="${numericId}"
                 data-position="${position}"
                 data-source="${source}"
                 data-price="${variant.price}"
                 data-compare-at-price="${variant.compare_at_price ?? ''}"
                 ${variant.available ? '' : 'disabled'}
                 aria-label="${label}${variant.available ? '' : ' - Sold out'}"
               >${label}</button>`;
             })
             .join('')}
         </div>`
      : '';

    return `
      <div class="cart-upsell__slide" data-position="${position}" data-product-id="${rec.product_id}" data-source="${source}">
        <article class="cart-items__table-row cart-upsell__row">
          <div class="cart-items__media">
            ${
              rec.image
                ? `<a href="${rec.url}" class="cart-items__media-container" style="--ratio: 0.8;" data-upsell-link data-variant-id="${rec.variant_id}" data-position="${position}" data-source="${source}">
                    <img class="cart-items__media-image border-style" src="${rec.image}" alt="${rec.image_alt || rec.product_title}" width="120" loading="${loading}" />
                  </a>`
                : ''
            }
          </div>
          <div class="cart-items__details cart-primary-typography">
            <p class="h4">
              <a href="${rec.url}" class="h4" data-upsell-link data-variant-id="${rec.variant_id}" data-position="${position}" data-source="${source}">
                ${primary}
              </a>
            </p>
            ${
              secondary
                ? `<p>${secondary}${colour ? `<br><span class="af-cart-colour">Colour: ${colour}</span>` : ''}</p>`
                : ''
            }
            <div data-cart-upsell-price>
              ${renderPriceHTML(price, compareAtPrice, rec.currency)}
            </div>
            ${variantButtons}
            <button
              type="button"
              class="button button--primary cart-upsell__add"
              data-variant-id="${numericVariantId}"
              data-position="${position}"
              data-source="${source}"
              data-multi-variant="${isSingleVariant ? 'false' : 'true'}"
              data-add-label="Add to cart"
              data-sold-out-label="Sold out"
              ${addDisabled ? 'disabled' : ''}
            >
              ${addDisabled ? 'Sold out' : 'Add to cart'}
            </button>
          </div>
        </article>
      </div>
    `;
  }

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-upsell-link]');
    if (link instanceof HTMLElement) {
      logClick(
        link.dataset.variantId || '',
        Number(link.dataset.position || 0),
        link.dataset.source
      );
      return;
    }

    const variantButton = target.closest('.cart-upsell__variant-button');
    if (variantButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (variantButton.disabled || variantButton.dataset.loading === 'true') return;

      const slide = variantButton.closest('.cart-upsell__slide');
      const priceContainer = slide?.querySelector('[data-cart-upsell-price]');
      if (priceContainer instanceof HTMLElement) {
        const variantPrice = parseFloat(variantButton.dataset.price || '0');
        const compareAtPrice = variantButton.dataset.compareAtPrice
          ? parseFloat(variantButton.dataset.compareAtPrice)
          : undefined;
        priceContainer.innerHTML = renderPriceHTML(
          variantPrice,
          compareAtPrice,
          window.afendsUpsell?.currency
        );
      }

      variantButton.dataset.loading = 'true';
      this.#setVariantsLoading(slide, true);
      await this.#addVariant(
        variantButton.dataset.variantId,
        Number(variantButton.dataset.position || 0),
        variantButton.dataset.source ||
          (slide instanceof HTMLElement ? slide.dataset.source : undefined)
      );
      this.#setVariantsLoading(slide, false);
      delete variantButton.dataset.loading;
      return;
    }

    const addButton = target.closest('.cart-upsell__add');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (addButton.disabled || addButton.dataset.loading === 'true') return;

      if (addButton.dataset.multiVariant === 'true') {
        const slide = addButton.closest('.cart-upsell__slide');
        const variants = slide?.querySelector('[data-cart-upsell-variants]');
        if (variants instanceof HTMLElement) {
          this.querySelectorAll('[data-cart-upsell-variants]').forEach((el) => {
            if (el !== variants) el.hidden = true;
          });
          this.querySelectorAll('.cart-upsell__add').forEach((button) => {
            if (button instanceof HTMLElement && button !== addButton) {
              button.hidden = false;
            }
          });
          variants.hidden = false;
          addButton.hidden = true;
        }
        return;
      }

      const originalText = addButton.textContent;
      addButton.dataset.loading = 'true';
      addButton.disabled = true;
      addButton.textContent = '…';

      await this.#addVariant(
        addButton.dataset.variantId,
        Number(addButton.dataset.position || 0),
        addButton.dataset.source
      );

      delete addButton.dataset.loading;
      addButton.disabled = false;
      addButton.textContent = originalText;
    }
  };

  /**
   * @param {Element | null | undefined} slide
   * @param {boolean} isLoading
   */
  #setVariantsLoading(slide, isLoading) {
    if (!(slide instanceof HTMLElement)) return;
    slide.querySelectorAll('.cart-upsell__variant-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (isLoading) {
        button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
      } else {
        button.disabled = button.dataset.wasDisabled === 'true';
        delete button.dataset.wasDisabled;
      }
    });
  }

  /**
   * @param {string | undefined} variantId
   * @param {number} position
   * @param {string | undefined} [source]
   */
  async #addVariant(variantId, position, source) {
    if (!variantId) return;

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    }).then((res) => res.json());

    if (response.status) {
      console.error(response.description || response.message || 'Failed to add upsell item');
      return;
    }

    logAdd(`gid://shopify/ProductVariant/${variantId}`, position, source);

    document.dispatchEvent(
      new CartAddEvent({}, 'cart-upsell', {
        source: 'cart-upsell',
        variantId,
        itemCount: 1,
        sections: response.sections,
      })
    );
  }
}

/**
 * @param {number} [delayMs]
 */
let globalRefreshTimer = null;
let globalRefreshRunning = false;
let globalRefreshQueued = false;

function scheduleGlobalRefresh(delayMs = 0) {
  clearTimeout(globalRefreshTimer);
  globalRefreshTimer = window.setTimeout(() => {
    runGlobalRefresh();
  }, delayMs);
}

async function runGlobalRefresh() {
  if (globalRefreshRunning) {
    globalRefreshQueued = true;
    return;
  }

  globalRefreshRunning = true;
  const token = ++refreshToken;

  try {
    do {
      globalRefreshQueued = false;

      const cart = await fetch('/cart.js').then((response) => response.json());
      if (token !== refreshToken) return;

      const cartKey = getCartKey(cart);
      const target = getLiveUpsell();

      if (!cart.items.length) {
        target?.applyRecs([], '');
        continue;
      }

      try {
        const cached = sessionStorage.getItem('afends_upsell_last_recs');
        if (cached && target) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed?.recs) && parsed.recs.length) {
            const cartHandles = new Set(cart.items.map((item) => item.handle));
            const pruned = parsed.recs.filter((rec) => !cartHandles.has(rec.product_handle));
            if (pruned.length) {
              target.applyRecs(pruned, cartKey);
            }
          }
        }
      } catch {
        /* ignore */
      }

      const { recs, meta } = await getUpsellRecs(cart, {
        onPartial: (partialRecs) => {
          if (token !== refreshToken) return;
          getLiveUpsell()?.applyRecs(partialRecs, cartKey, null);
        },
      });

      if (token !== refreshToken) return;
      getLiveUpsell()?.applyRecs(recs, cartKey, meta);
    } while (globalRefreshQueued && token === refreshToken);
  } catch (error) {
    console.error('Cart upsell refresh failed', error);
  } finally {
    globalRefreshRunning = false;
    if (globalRefreshQueued) {
      globalRefreshQueued = false;
      scheduleGlobalRefresh(0);
    }
  }
}

/** @returns {CartUpsellComponent | null} */
function getLiveUpsell() {
  if (activeUpsell instanceof CartUpsellComponent) return activeUpsell;
  const el = document.querySelector('cart-upsell-component');
  return el instanceof CartUpsellComponent ? el : null;
}

function prefetchUpsellPools() {
  if (poolsPrefetchStarted || !window.afendsUpsell?.pools) return;
  poolsPrefetchStarted = true;

  const country = window.afendsUpsell.country || window.afendsUpsell.market || 'AU';
  const poolNames = Object.keys(window.afendsUpsell.pools);

  const run = () => {
    poolNames.forEach((poolName) => {
      hydratePool(poolName, country).catch(() => {});
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 300);
  }
}

/**
 * @param {object | null} [existingCart]
 * @param {{ onPartial?: (recs: UpsellCandidate[]) => void }} [options]
 * @returns {Promise<{ recs: UpsellCandidate[], meta: Record<string, unknown> | null }>}
 */
async function getUpsellRecs(existingCart = null, options = {}) {
  const cart = existingCart || (await fetch('/cart.js').then((response) => response.json()));
  if (!cart.items.length) return { recs: [], meta: null };

  const config = window.afendsUpsell;
  const market = config.market || 'AU';
  const country = config.country || market;
  const bucket = getBucket(market);
  const thresholdConfig = config.thresholds?.[market] || config.thresholds?.AU;
  const cartTotalLocal = cart.total_price / 100;
  const gap = thresholdConfig.amount - cartTotalLocal;

  const cartCategories = new Set();
  const cartGenders = new Set();
  const cartVariantIds = new Set();
  const cartProductIds = [];
  const cartProductHandles = new Set();
  const wearWithIds = [];

  // Fast path from cart line data (no network) so pools can start immediately.
  cart.items.forEach((item) => {
    const category =
      normalizeCategory(item.product_type) ||
      normalizeCategory(item.product_title) ||
      normalizeCategory(item.title);
    const gender = normalizeGender(null, item.product_title || item.title);
    if (category) cartCategories.add(category);
    if (gender) cartGenders.add(gender);
    cartVariantIds.add(`gid://shopify/ProductVariant/${item.variant_id}`);
    cartProductIds.push(`gid://shopify/Product/${item.product_id}`);
    cartProductHandles.add(item.handle);
  });

  const productGids = [...cartProductIds];
  const quickDominantGender = computeDominantGender(cartGenders);
  const quickDecision = decisionTree(bucket, quickDominantGender, cartCategories);

  const metafieldsPromise = fetchCartProductMetafields(productGids, country);
  const attachScoresPromise = fetchAttachScores(cartProductIds, bucket);
  const quickPoolsPromise = Promise.all([
    hydratePool(quickDecision.primary, country),
    hydratePool(quickDecision.secondary, country),
  ]);

  // Paint as soon as quick pools are ready (usually cached after prefetch).
  const [quickPrimary, quickSecondary] = await quickPoolsPromise;
  const quickPrimaryFiltered = filterCandidatesWithFallback(
    quickPrimary,
    cartVariantIds,
    cartProductHandles,
    gap,
    4
  );
  const quickSecondaryFiltered = filterCandidatesWithFallback(
    quickSecondary,
    cartVariantIds,
    cartProductHandles,
    gap,
    4
  );
  const quickRecs = composeSlots(
    null,
    quickPrimaryFiltered,
    quickSecondaryFiltered,
    quickDecision.split
  ).map((rec) => ({ ...rec, all_variants: [] }));
  if (quickRecs.length) options.onPartial?.(quickRecs);

  const metafieldsByProduct = await metafieldsPromise;

  cartCategories.clear();
  cartGenders.clear();
  wearWithIds.length = 0;

  cart.items.forEach((item) => {
    const productGid = `gid://shopify/Product/${item.product_id}`;
    const metafields = metafieldsByProduct[productGid] || item.product_metafields?.custom || {};

    const category =
      normalizeCategory(metafields.product_category) ||
      normalizeCategory(item.product_type) ||
      normalizeCategory(item.product_title) ||
      normalizeCategory(item.title);
    const gender = normalizeGender(metafields.gender, item.product_title || item.title);

    if (category) cartCategories.add(category);
    if (gender) cartGenders.add(gender);

    const wearWith = metafields.complete_the_looks;
    if (Array.isArray(wearWith)) wearWithIds.push(...wearWith);
  });

  const dominantGender = computeDominantGender(cartGenders);
  const { primary, secondary, split, categoryMissing } = decisionTree(bucket, dominantGender, cartCategories);

  const poolsChanged =
    primary !== quickDecision.primary || secondary !== quickDecision.secondary;

  const [primaryCandidates, secondaryCandidates, attachScores, wearWithHydrated] = await Promise.all([
    poolsChanged ? hydratePool(primary, country) : Promise.resolve(quickPrimary),
    poolsChanged ? hydratePool(secondary, country) : Promise.resolve(quickSecondary),
    attachScoresPromise,
    wearWithIds.length > 0
      ? hydrateWearWithProducts([...new Set(wearWithIds)], country)
      : Promise.resolve([]),
  ]);

  const priorityRec =
    wearWithHydrated.length > 0
      ? selectPriorityRec(wearWithHydrated, cartVariantIds, cartProductHandles, gap, primary)
      : null;

  const rankedPrimary = applyAttachBoost(primaryCandidates, cartProductIds, attachScores);
  const rankedSecondary = applyAttachBoost(secondaryCandidates, cartProductIds, attachScores);

  const filteredPrimary = filterCandidatesWithFallback(
    rankedPrimary,
    cartVariantIds,
    cartProductHandles,
    gap,
    4
  );
  const filteredSecondary = filterCandidatesWithFallback(
    rankedSecondary,
    cartVariantIds,
    cartProductHandles,
    gap,
    4
  );

  const finalRecs = composeSlots(priorityRec, filteredPrimary, filteredSecondary, split).map((rec) => ({
    ...rec,
    all_variants: [],
  }));

  options.onPartial?.(finalRecs);

  const variantMap = await fetchProductVariants(
    [...new Set(finalRecs.map((rec) => rec.product_id))],
    country
  );

  const enrichedRecs = finalRecs.map((rec) => ({
    ...rec,
    all_variants: variantMap[rec.product_id] || [],
  }));

  return {
    recs: enrichedRecs,
    meta: {
      bucket,
      market,
      currency: config.currency || thresholdConfig.currency || cart.currency,
      pool_primary: primary,
      pool_secondary: secondary,
      split,
      priority_source: priorityRec ? 'complete_the_looks' : null,
      cart_total: cartTotalLocal,
      threshold: thresholdConfig.amount,
      gap,
      cart_variant_ids: [...cartVariantIds],
      cart_product_ids: cartProductIds,
      cart_categories: [...cartCategories],
      cart_genders: [...cartGenders],
      variants_shown: enrichedRecs.map((variant, index) => ({
        variant_id: variant.variant_id,
        product_id: variant.product_id,
        position: index + 1,
        price: variant.price,
        source: variant.source,
      })),
      category_missing: Boolean(categoryMissing),
    },
  };
}

/**
 * @param {{ items?: Array<{ variant_id: number }> }} cart
 */
function getCartKey(cart) {
  return (cart.items || [])
    .map((item) => item.variant_id)
    .sort((a, b) => a - b)
    .join(',');
}

const UPSELL_SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '20', '22', '24', '26', '28', '30', '32', '34', '36', '38', '40'];

/**
 * @param {NonNullable<UpsellCandidate['all_variants']>} variants
 */
function sortUpsellVariants(variants) {
  return [...variants].sort((a, b) => {
    const aLabel = String(a.size || a.title || '');
    const bLabel = String(b.size || b.title || '');
    const aIndex = UPSELL_SIZE_ORDER.indexOf(aLabel);
    const bIndex = UPSELL_SIZE_ORDER.indexOf(bLabel);
    if (aIndex === -1 && bIndex === -1) return aLabel.localeCompare(bLabel, undefined, { numeric: true });
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
